import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';

import PageTemplate from 'components/general/PageTemplate';

import { refreshReloadPrompt } from 'hooks';
import { getClinics, updateClinicStatus } from 'api/clinic';
import StatusDropdown from 'components/StatusDropdown';
import { siteNameGenerator } from 'helpers/site-name-generator';
import Clinic from './Clinic';
import {
  columns,
  suggestionPrimaryKey,
  suggestionSecondaryKeys,
} from './clinic.config';

const Clinics = () => {
  const { path } = useRouteMatch();
  const history = useHistory();

  const [modalOpen, setModalOpen] = useState(true);
  const [clinics, setClinics] = useState();
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [rowSelected, setRowSelected] = useState();
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [blockRouteChange, setBlockRouteChange] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  const handleStatusMenuOpen = () => {
    setStatusMenuOpen(true);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuOpen(false);
  };

  const fetchClinics = async () => {
    setLoading(true);
    const { data } = await getClinics(pageNumber, pageSize);
    setLoading(false);

    setClinics(data.data);
    setRowCount(data.pagination.total);
  };

  useEffect(async () => {
    setLoading(true);

    await fetchClinics();

    setLoading(false);
  }, [pageNumber, pageSize]);

  const handleModalOpen = ({ id }) => {
    history.push(`/clinics/${id}`);
    setModalOpen(true);
  };

  const handleAdd = () => {
    history.push('/clinics/add');
    setModalOpen(true);
    fetchClinics();
  };

  const handleModalClose = (save) => {
    if (save && typeof save === 'boolean') {
      history.block(() => true);
      setModalOpen(false);
    }

    if (!blockRouteChange && !save && typeof save !== 'boolean') {
      setModalOpen(false);
    }

    history.push('/clinics');
    fetchClinics();
    window.removeEventListener('beforeunload', refreshReloadPrompt);
  };

  const handleStatusChange = async (value) => {
    const {
      params: { id },
    } = rowSelected;

    await updateClinicStatus(id, value);
    await fetchClinics();

    handleStatusMenuClose();
  };

  const formattedClinicData =
    clinics &&
    clinics.map((clinic) => ({
      ...clinic,
      organization_name: clinic.organization.name,
      service_name: clinic.service.name,
      site_name: siteNameGenerator(clinic.organization, clinic.sublocation),
      address: clinic.sublocation.address,
      city: clinic.sublocation.city,
      state: clinic.sublocation.state,
      country: clinic.sublocation.country,
      start_date: moment(clinic.start_date).format('MM-DD-YYYY'),
      end_date: moment(clinic.end_date).format('MM-DD-YYYY'),
      start_time: moment(clinic.start_time, 'hh:mm:ss').format('h:mma'),
      end_time: moment(clinic.end_time, 'hh:mm:ss').format('h:mma'),
    }));

  const dataCount = clinics && clinics.length;

  return (
    <>
      {statusMenuOpen && (
        <StatusDropdown
          rowSelected={rowSelected}
          handleClickAway={handleStatusMenuClose}
          handleStatusChange={handleStatusChange}
          dataCount={dataCount}
        />
      )}

      <PageTemplate
        data={formattedClinicData || []}
        columns={columns}
        suggestionPrimaryKey={suggestionPrimaryKey}
        suggestionSecondaryKeys={suggestionSecondaryKeys}
        handleModalOpen={handleModalOpen}
        setPageNumber={setPageNumber}
        setPageSize={setPageSize}
        rowCount={rowCount}
        pageNumber={pageNumber}
        pageSize={pageSize}
        loading={loading}
        handleAdd={handleAdd}
        setModalOpen={setModalOpen}
        setRowSelected={setRowSelected}
        toggleMenu={handleStatusMenuOpen}
        enableCopy
        fetchClinics={fetchClinics}
        statusMenuOpen={statusMenuOpen}
      />

      <Switch>
        <Route path={`${path}/add`}>
          <Clinic
            modalOpen={modalOpen}
            handleModalClose={handleModalClose}
            blockRouteChange={blockRouteChange}
            setBlockRouteChange={setBlockRouteChange}
          />
        </Route>

        <Route path={`${path}/:id`}>
          <Clinic
            modalOpen={modalOpen}
            handleModalClose={handleModalClose}
            blockRouteChange={blockRouteChange}
            setBlockRouteChange={setBlockRouteChange}
          />
        </Route>
      </Switch>
    </>
  );
};

export default Clinics;
